import React, {useEffect, useState} from "react";
import {Panel} from 'components/panel';
import PropTypes from "prop-types";
import { Dropdown } from "components/dropdown";
import './InfoPanel.css';
import moment from "moment";
import { Icon, Intent, MenuItem, Tooltip } from "@blueprintjs/core";
import classNames from 'classnames';
import { Comment } from "components/comments";
import { AssetService } from "services";
import './ActivitiesList.css';
import './HarshBrakingPanel.css';
import { NotificationToaster } from "components/notifications";
import { DateToLocal } from "components/dates/DateToLocal";

export function InfoPanel(
    {
        showBurger = false,
        isSummary = false,
        disabled = false,
        cardIwId = null,
        onCommentChange = () => { },
        allowAddComment = false,
        burgerItems,
        children,
        drive,
        driverName,
        endDate,
        endOdo,
        headingText,
        initialComment,
        leftColor,
        panelClassNames,
        poa,
        rest,
        startDate,
        startOdo,
        work
    }
) {

    const [showCommentComponent, setShowCommentComponent] = useState(false);
    const [allBurgerItems, setAllBurgerItems] = useState([<MenuItem text="Initial State" key={`${cardIwId}-mi-initial`} />]);
    const titleMaxLength = 60;

    function formatTime(intTime){
        if (intTime === undefined || intTime === null)
            return "";
       return `${Math.floor(intTime / 60)}h ${intTime % 60}m`;
    }
    function HandleSaveComment(commentToSave){
        AssetService.upsertComment(cardIwId, commentToSave)
        .then(() => {         
            onCommentChange();
            NotificationToaster.show(Intent.SUCCESS, `Your comment has been successfully updated`, false);
        })
        .catch((error) => {
            NotificationToaster.show(Intent.DANGER, error, false);
        });
    }
    
    function HandleDeleteComment(){
        AssetService.deleteComment(cardIwId)
        .then(() => {
            onCommentChange();
            NotificationToaster.show(Intent.SUCCESS, `Your comment has been successfully deleted`, false);
            setShowCommentComponent(false);
        })
        .catch((error) => {
            NotificationToaster.show(Intent.DANGER, error, false);
        });

    }

    useEffect(() => {
        let items = burgerItems ? [...burgerItems] : [];
        if (allowAddComment || initialComment) {
            items.push(<MenuItem text="Add Comment" key={`${cardIwId}-mi-comment`} onClick={() => setShowCommentComponent(true)} />);
        }
        setShowCommentComponent(initialComment);
        setAllBurgerItems(items);
    }, [burgerItems, allowAddComment, initialComment]);

    return (
        <Panel className={classNames("info-panel", panelClassNames)} color={leftColor} disabled={disabled} >
            {
                headingText.length < titleMaxLength ? 
                    <h3 className="info-panel-header">{headingText} {initialComment && <Icon icon="comment" size={14} />}</h3>
                    : 
                    <Tooltip content={headingText} position="top">
                        <h3 className="info-panel-header">{headingText.substring(0, titleMaxLength)}... {initialComment && <Icon icon="comment" size={14} />}</h3>
                    </Tooltip>
            }
            {
                showBurger && allBurgerItems.length > 0 &&
                    <div className="info-panel-button">
                        <Dropdown icon="menu" iconOnly={true} showArrow={false} minimal={true} large={false} items={allBurgerItems} popoverUsePortal={false} popoverPosition="BOTTOM_RIGHT" />
                    </div>
            }
            <div className="info-panel-content">
                <h4>{isSummary ? 
                    `${moment(startDate).format("DD MMM YYYY")}` :
                    <><DateToLocal format="DD MMM YYYY HH:mm">{startDate}</DateToLocal> - <DateToLocal format="DD MMM YYYY HH:mm">{endDate}</DateToLocal></>}
                    {driverName ? ` (${String(driverName).toLocaleLowerCase()})`: ""}
                </h4>
                <table className="info-panel-table">
                    <tbody>
                        <tr>
                            <th><Icon icon={<img src="/icons/activity-types/driving.png" className="vu-activity-icon-small" alt="Drive" />} size={10} /><div className="vu-activity-name">Drive</div></th>
                            <th><Icon icon={<img src="/icons/activity-types/work.png" className="vu-activity-icon-small" alt="Work" />} size={10} /><div className="vu-activity-name">Work</div></th>
                            <th><Icon icon={<img src="/icons/activity-types/break.png" className="vu-activity-icon-small" alt="Rest" />} size={10} /><div className="vu-activity-name">Rest</div></th>
                            <th><Icon icon={<img src="/icons/activity-types/available.png" className="vu-activity-icon-small" alt="Available" />} size={10} /><div className="vu-activity-name">Available</div></th>
                        </tr>
                        <tr>
                            <td>{formatTime(drive)}</td>
                            <td>{formatTime(work)}</td>
                            <td>{formatTime(rest)}</td>
                            <td>{formatTime(poa)}</td>
                        </tr>
                        {startOdo && endOdo && 
                        <>
                            <tr>
                                <th colSpan={4}>Odometer</th>
                            </tr>
                            <tr>
                                <td colSpan={4}>{String(startOdo ?? "").padStart(6, '0')} - {String(endOdo ?? "").padStart(6, '0')} Km</td>
                            </tr>
                        </>
                        }
                    </tbody>
                </table>
                {showCommentComponent &&
                    <Comment onSave={HandleSaveComment} onDelete={HandleDeleteComment} value={initialComment} id={`${cardIwId}-comment-panel`} isEditibleInitially={!initialComment} className="info-panel-comment" />
                }
            </div>
            {children}
        </Panel>
    );
}

InfoPanel.propTypes = {
    showBurger: PropTypes.bool,
    cardIwId: PropTypes.number,
    burgerItems: PropTypes.array,
    headingText: PropTypes.string.isRequired,
    leftColor: PropTypes.string,
    isSummary: PropTypes.bool,
    startDate: PropTypes.string.isRequired,
    endDate: PropTypes.string,
    driverName: PropTypes.string,
    drive: PropTypes.number,
    work: PropTypes.number,
    rest: PropTypes.number,
    poa: PropTypes.number,
    startOdo: PropTypes.number,
    endOdo: PropTypes.number,
    disabled: PropTypes.bool,
    panelClassNames: PropTypes.string,
    initialComment: PropTypes.string,
    onCommentChange: PropTypes.func,
    allowAddComment: PropTypes.bool
};